import { useEffect, useMemo, useState } from "react";
import { DatePicker, Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { CalendarOutlined, PercentageOutlined } from "@ant-design/icons";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";

// Shared Inventory-style components — same ones used by BankStatement.jsx,
// PaymentReciept.jsx and PurshaseRegister.jsx. Adjust the path if your
// project's shared folder differs.
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";

const { RangePicker } = DatePicker;

/* ─── filter box theme palette (same as the other Accounts report screens) ─── */
const FILTER_THEMES = {
  blue: {
    text: "#1d4ed8",
    bg: "#ffffff",
    border: "#93c5fd",
    ring: "rgba(29,78,216,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "blue", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "SMCode", label: "SM Code" },
  { key: "TOKEN", label: "Token" },
];
const secondaryFields = [
  { key: "QTY", label: "Qty" },
  { key: "COMM", label: "Commission" },
  { key: "AMOUNT", label: "Amount" },
];

const SalesMenWiseCommission = () => {
  const [rawData, setRawData] = useState([]);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  /* ---- data / API logic (unchanged endpoint + query params/format) ---- */
  useEffect(() => {
    if (!dates[0] || !dates[1]) return;

    setLoading(true);
    setTableVisible(false);

    const billStartDate = dayjs(dates[0]).format("MM/DD/YYYY");
    const billEndDate = dayjs(dates[1]).format("MM/DD/YYYY");

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetSMCommissionDetails?billStartDate=${billStartDate}&billEndDate=${billEndDate}`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = response.data;
        const calculatedData = detailsData.map((item, index) => ({
          ...item,
          key: index + 1,
          serialNo: index + 1,
        }));
        setRawData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching salesmen commission details:", error);
      })
      .finally(() => setLoading(false));
  }, [dates]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "SM Code",
      dataIndex: "SMCode",
      key: "SMCode",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    { title: "Token", dataIndex: "TOKEN", key: "TOKEN" },
    {
      title: "QTY",
      dataIndex: "QTY",
      key: "QTY",
      align: "right",
      render: (value) => Number(value || 0),
    },
    {
      title: "COMM",
      dataIndex: "COMM",
      key: "COMM",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "Amount",
      dataIndex: "AMOUNT",
      key: "AMOUNT",
      align: "right",
      render: (value) => (
        <strong style={{ color: "#0f766e" }}>
          {Number(value || 0).toFixed(2)}
        </strong>
      ),
    },
  ];

  const totals = useMemo(() => {
    return rawData.reduce(
      (acc, item) => {
        acc.qty += Number(item.QTY || 0);
        acc.comm += Number(item.COMM || 0);
        acc.amount += Number(item.AMOUNT || 0);
        return acc;
      },
      { qty: 0, comm: 0, amount: 0 },
    );
  }, [rawData]);

  const formattedData = useMemo(
    () =>
      rawData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        QTY: Number(item.QTY || 0),
        COMM: Number(item.COMM || 0),
        AMOUNT: Number(item.AMOUNT || 0),
      })),
    [rawData],
  );

  const exportData = [
    ...formattedData.map((item) => ({
      ...item,
      QTY: item.QTY,
      COMM: item.COMM.toFixed(2),
      AMOUNT: item.AMOUNT.toFixed(2),
    })),
    {
      serialNo: "Total",
      SMCode: "",
      TOKEN: "",
      QTY: totals.qty,
      COMM: totals.comm.toFixed(2),
      AMOUNT: totals.amount.toFixed(2),
    },
  ];

  return (
    <div className={styles.page}>
      <style>{`
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>

      <InventoryPageHeader
        title="SalesMen Wise Commission"
        subtitle="Accounts · SalesMen Wise Commission"
        icon={
          <PercentageOutlined style={{ color: "#fff", fontSize: 16 }} />
        }
        recordCount={rawData.length}
        visible={rawData.length > 0}
        stats={[
          { label: "Records", value: rawData.length, color: "blue" },
          { label: "Qty", value: totals.qty, color: "amber" },
          {
            label: "Commission",
            value: totals.comm.toFixed(2),
            color: "green",
          },
          {
            label: "Amount",
            value: totals.amount.toFixed(2),
            color: "teal",
          },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<CalendarOutlined />}
            label="Date Range"
            theme="blue"
          >
            <RangePicker
              value={dates}
              onChange={(range) => {
                if (range?.[0] && range?.[1]) setDates(range);
              }}
              format="DD/MM/YYYY"
              size="middle"
              bordered={false}
              allowClear={false}
              style={{ width: 240 }}
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), "day")
              }
            />
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="SalesMenWiseCommission"
            totals={{
              totalDebit: totals.qty,
              totalCredit: totals.comm.toFixed(2),
              totalBalance: totals.amount.toFixed(2),
            }}
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="smwc-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No commission records found for the selected date range"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row
            style={{ background: "#f5f5f5", fontWeight: 600 }}
          >
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">
              {totals.qty}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.comm.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.amount.toFixed(2)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default SalesMenWiseCommission;